import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { fetchVehicleModelCopyAdminList, buildVehicleModelCopyExportUrl } from "../../../../services/vehicleModelCopyAdminApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import VehicleModelCopyEditorPanel from "./VehicleModelCopyEditorPanel";
import VehicleModelCopyImportPanel from "./VehicleModelCopyImportPanel";

const LOCALES = ["en-US", "es-CO", "ru-RU"];

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function localeBadge(localeState) {
  if (!localeState?.exists) return "Missing";
  if (localeState.status === "published") return "Published";
  if (localeState.status === "draft") return "Draft";
  return "—";
}

function publicSourceLabel(source) {
  switch (source) {
    case "published_db":
      return "DB";
    case "draft_only":
      return "Draft";
    default:
      return "None";
  }
}

const VehicleModelCopyPage = ({ onClose, onOpenQuality }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canView =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW);
  const canEdit =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);
  const canPublish = canEdit;

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [locale, setLocale] = useState("");
  const [coverage, setCoverage] = useState("");
  const [linkState, setLinkState] = useState("linked");
  const [publicSource, setPublicSource] = useState("");
  const [selected, setSelected] = useState(null);
  const [showImportPanel, setShowImportPanel] = useState(false);

  const exportFilters = {
    makeKey: undefined,
    modelKey: undefined,
    locale: locale || undefined,
    status: status || undefined,
  };

  const load = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchVehicleModelCopyAdminList({
        search: search.trim() || undefined,
        status: status || undefined,
        locale: locale || undefined,
        coverage: coverage || undefined,
        linkState,
        publicSource: publicSource || undefined,
        limit: pagination.limit,
        offset: pagination.offset,
      });
      setItems(data.items);
      setPagination(data.pagination);
    } catch (error) {
      setItems([]);
      setErrorMessage(error.message || "Unable to load vehicle model copy.");
    } finally {
      setLoading(false);
    }
  }, [
    canView,
    search,
    status,
    locale,
    coverage,
    linkState,
    publicSource,
    pagination.limit,
    pagination.offset,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelect = (item) => {
    if (selected?.vehicleModelId === item.vehicleModelId && selected?.makeKey === item.makeKey) {
      return;
    }
    setSelected({
      vehicleModelId: item.vehicleModelId,
      makeKey: item.makeKey,
      modelKey: item.modelKey,
      makeName: item.makeName,
      modelName: item.modelName,
    });
  };

  if (!canView) {
    return (
      <AdminStatePanel
        title="Vehicle model copy"
        message="You do not have permission to view vehicle model copy."
      />
    );
  }

  return (
    <div className="admin-dashboard admin-vehicle-model-copy-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <p className="admin-dashboard__eyebrow">Mobility · Vehicle Intelligence</p>
          <h1 className="admin-dashboard__title">Vehicle Model Copy</h1>
          <p className="admin-dashboard__subtitle">
            Manage localized vehicle model descriptions, drafts, and publication state.
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          {canView ? (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={() => onOpenQuality?.()}
            >
              Copy quality
            </button>
          ) : null}
          <a
            className="admin-dashboard__action-btn"
            href={buildVehicleModelCopyExportUrl("csv", exportFilters)}
          >
            Export CSV
          </a>
          <a
            className="admin-dashboard__action-btn"
            href={buildVehicleModelCopyExportUrl("json", exportFilters)}
          >
            Export JSON
          </a>
          {canEdit ? (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={() => setShowImportPanel((current) => !current)}
            >
              {showImportPanel ? "Hide import" : "Bulk import"}
            </button>
          ) : null}
          <button type="button" className="admin-dashboard__action-btn" onClick={load}>
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? (
        <p className="admin-dashboard__toast" role="status">
          {toast}
        </p>
      ) : null}

      <section className="panel-card admin-vehicle-model-copy-dash__filters">
        <div className="admin-vehicle-model-copy-dash__filter-row">
          <label>
            Search
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setPagination((current) => ({ ...current, offset: 0 }));
                setSearch(event.target.value);
              }}
              placeholder="Make, model, or vehicle model ID"
            />
          </label>
          <label>
            Status
            <select
              value={status}
              onChange={(event) => {
                setPagination((current) => ({ ...current, offset: 0 }));
                setStatus(event.target.value);
              }}
            >
              <option value="">Any</option>
              <option value="missing">Missing copy</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </label>
          <label>
            Locale
            <select
              value={locale}
              onChange={(event) => {
                setPagination((current) => ({ ...current, offset: 0 }));
                setLocale(event.target.value);
              }}
            >
              <option value="">All locales</option>
              {LOCALES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label>
            Coverage
            <select
              value={coverage}
              onChange={(event) => {
                setPagination((current) => ({ ...current, offset: 0 }));
                setCoverage(event.target.value);
              }}
            >
              <option value="">Any</option>
              <option value="complete">Complete</option>
              <option value="partial">Partial</option>
              <option value="missing">Missing</option>
            </select>
          </label>
          <label>
            Link state
            <select
              value={linkState}
              onChange={(event) => {
                setPagination((current) => ({ ...current, offset: 0 }));
                setLinkState(event.target.value);
              }}
            >
              <option value="linked">Linked</option>
              <option value="unlinked">Unlinked</option>
            </select>
          </label>
          <label>
            Public source
            <select
              value={publicSource}
              onChange={(event) => {
                setPagination((current) => ({ ...current, offset: 0 }));
                setPublicSource(event.target.value);
              }}
            >
              <option value="">Any</option>
              <option value="published_db">DB</option>
              <option value="no_description">No description</option>
            </select>
          </label>
        </div>
      </section>

      {showImportPanel && canEdit ? (
        <VehicleModelCopyImportPanel canApply={canEdit} filters={exportFilters} />
      ) : null}

      {errorMessage ? (
        <AdminStatePanel title="Unable to load copy" message={errorMessage} onRetry={load} />
      ) : null}

      <div className="admin-vehicle-model-copy-dash__layout">
        <AdminTableContainer>
          {loading ? <p className="muted">Loading vehicle model copy…</p> : null}
          {!loading && !items.length ? <p className="admin-table__empty">No models found.</p> : null}
          {!loading && items.length ? (
            <table className="admin-data-health-table">
              <thead>
                <tr>
                  <th scope="col">Make</th>
                  <th scope="col">Model</th>
                  <th scope="col">Canonical key</th>
                  <th scope="col">Link</th>
                  {LOCALES.map((item) => (
                    <th key={item} scope="col">
                      {item}
                    </th>
                  ))}
                  <th scope="col">Public</th>
                  <th scope="col">Updated</th>
                  <th scope="col">Updated by</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const rowKey = item.vehicleModelId || `${item.makeKey}|${item.modelKey}`;
                  const selectedRow =
                    selected?.vehicleModelId === item.vehicleModelId &&
                    selected?.makeKey === item.makeKey &&
                    selected?.modelKey === item.modelKey;
                  return (
                    <tr key={rowKey} className={selectedRow ? "is-selected" : undefined}>
                      <td>{item.makeName}</td>
                      <td>{item.modelName}</td>
                      <td>
                        {item.makeKey}|{item.modelKey}
                      </td>
                      <td>{item.linkState}</td>
                      {LOCALES.map((localeKey) => (
                        <td key={localeKey}>{localeBadge(item.locales?.[localeKey])}</td>
                      ))}
                      <td>{publicSourceLabel(item.publicSource)}</td>
                      <td>{formatDate(item.updatedAt)}</td>
                      <td>{item.updatedBy?.displayName || "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="admin-dashboard__action-btn"
                          onClick={() => handleSelect(item)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </AdminTableContainer>

        {selected ? (
          <VehicleModelCopyEditorPanel
            selection={selected}
            canEdit={canEdit}
            canPublish={canPublish}
            onClose={() => setSelected(null)}
            onUpdated={() => {
              setToast("Copy updated.");
              load();
            }}
          />
        ) : null}
      </div>

      <footer className="admin-vehicle-model-copy-dash__footer">
        <p className="muted">
          Showing {items.length} of {pagination.total} models
        </p>
        <div className="admin-vehicle-model-copy-dash__pager">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            disabled={pagination.offset <= 0}
            onClick={() =>
              setPagination((current) => ({
                ...current,
                offset: Math.max(0, current.offset - current.limit),
              }))
            }
          >
            Previous
          </button>
          <button
            type="button"
            className="admin-dashboard__action-btn"
            disabled={pagination.offset + pagination.limit >= pagination.total}
            onClick={() =>
              setPagination((current) => ({
                ...current,
                offset: current.offset + current.limit,
              }))
            }
          >
            Next
          </button>
        </div>
      </footer>
    </div>
  );
};

export default VehicleModelCopyPage;
